import React from "react";
import { Box, Typography } from "@mui/material";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import { getTableDataFromApi } from "../Components/FleetChartDrillDown/drillDownHelpers";

import {
  getFleetPerformanceAnalyticsList,
  getFleetPerformanceAnalyticsListActions,
} from "../../../Api/FleetPerformanceApi";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { FLEET_BREAKDOWN_DIMENSIONS } from "../fleetBreakdownDimensions";

export const ENERGY_CONSUMPTION_BUCKETS = [
  { backendValue: "< 1.25",     label: "< 1.25 kWh/km",       color: "#1B5E20" },
  { backendValue: "1.25 - 1.5", label: "1.25 - 1.5 kWh/km",   color: "#00C853" },
  { backendValue: "> 1.5",      label: "> 1.5 kWh/km",         color: "#FFB300" },
];

const bucketLabelToBackend = Object.fromEntries(
  ENERGY_CONSUMPTION_BUCKETS.map((b) => [b.label, b.backendValue])
);

// The drilldown groups and filters by whatever dimension the chart was showing
// when the bar was clicked, so it has to know every dimension a menu can offer -
// otherwise a configured dimension falls through to the originCity default below
// and the drilldown quietly filters by a field the user did not pick. Derived
// from the one catalogue the menus resolve from; ProjectName is added on top
// because the trends charts drill down by project rather than by a dimension.
const GROUP_BY_MAP = {
  ...Object.fromEntries(FLEET_BREAKDOWN_DIMENSIONS.map((d) => [d.value, d.value])),
  ProjectName: "ProjectName",
};

// Exported for the test that guards the carry-through: a dimension the menu can
// offer must map to itself here, or the drilldown filters by something else.
export const FLEET_DRILLDOWN_GROUP_BY_MAP = GROUP_BY_MAP;

const getSelectedLabels = (selectedItems) => {
  if (
    !selectedItems ||
    selectedItems.length === 0 ||
    selectedItems.some((i) => i.id === "all")
  ) {
    return "All";
  }
  if (selectedItems.length === 1) {
    return selectedItems[0].label;
  }
  const allLabels = selectedItems.map((i) => i.label).join(", ");
  const displayText = `${selectedItems[0].label} + ${selectedItems.length - 1}`;
  return <PrimaryTooltip title={allLabels}>{displayText}</PrimaryTooltip>;
};

/**
 * The drilldown hook injects a resolver that adds whatever the org's configured
 * columns need on top of the list a chart already asks for. Without it - an org
 * that configures nothing, or a caller that does not inject - the chart's own
 * list stands unchanged.
 */
export const withConfiguredColumns = (options, baseFields) =>
  (options?.resolveMetaInfo || ((fields) => fields))(baseFields);

export const FLEET_CHART_CONFIG = {
  ENERGY_CONSUMPTION_BREAKDOWN: {
    // Level 1: Summary Table
    orgMetaTableKeys: { 2: "energyConsumptionBreakdownVehiclesTable" },
    getTableConfig: (level, dataPoint) => {
      if (level === 2) {
        return {
          title: "Vehicles Affected",
          disableFiltersSettings: false,
          disableColumnSettings: false,
          tableColumnsConfig: {
            licencePlatNumber: {
              title: "Bus Number",
              dataIndex: "licencePlatNumber",
              minWidth: 150,
              valueRenderingType: "device-id-link",
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
              fixed: "left",
            },
            AveragekWhPerKm: {
              title: "Avg. Energy Consumed per Km (kWh/km)",
              dataIndex: "AveragekWhPerKm",
              valueRenderingType: "number",
              decimalPlaces: 2,
              minWidth: 200,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            bounds: {
              title: "Bounds of Energy Consumed per Km (kWh/km)",
              dataIndex: "Bounds of Energy Consumed per Km (kWh/km)",
              valueRenderingType: "number",
              minWidth: 250,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
              sortKey: "AveragekWhPerKm",
            },
            originCity: {
              title: "City",
              dataIndex: "originCity",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originDepot: {
              title: "Depot",
              dataIndex: "originDepot",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originState: {
              title: "State",
              dataIndex: "originState",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
          },
          tableColumnOrder: [
            "licencePlatNumber",
            "AveragekWhPerKm",
            "bounds",
            "originDepot",
            "originCity",
            "originState",
          ],
          filtersConfig: [
            { key: "originState", type: "dynamic", label: "State", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originState" } },
            { key: "originCity", type: "dynamic", label: "City", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originCity" } },
            { key: "originDepot", type: "dynamic", label: "Depot", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originDepo" } },
            { key: "AveragekWhPerKm", type: "static", label: "Energy Consumption", staticData: [], displayType: "range", unitOfMeasurement: "kWh/km" },
          ],
        };
      }
      const groupByLabel =
        dataPoint?.groupBy?.label?.replace("By ", "") || "City";
      const selectedLabels = getSelectedLabels(dataPoint?.selectedItems);
      const project = dataPoint?.projectName || "";

      return {
        title: (
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            <Typography
              component="span"
              variant="fw700s18lh24c000000"
              sx={{ mr: 0.5, color: "#000" }}
            >
              {selectedLabels}:
            </Typography>
            <Typography
              component="span"
              variant="fw700s18lh24c000000"
              sx={{ color: "#000" }}
            >
              {project}
            </Typography>
          </Box>
        ),
        tableColumnsConfig: {
          col1: {
            title: groupByLabel,
            dataIndex: "col1",
            minWidth: 150,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: GROUP_BY_MAP[dataPoint?.groupBy?.value] || "originCity",
          },
          excellent: {
            title: ENERGY_CONSUMPTION_BUCKETS[0].label,
            dataIndex: "excellent",
            minWidth: 200,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "< 1.25",
          },
          good: {
            title: ENERGY_CONSUMPTION_BUCKETS[1].label,
            dataIndex: "good",
            minWidth: 200,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "1.25 - 1.5",
          },
          poor: {
            title: ENERGY_CONSUMPTION_BUCKETS[2].label,
            dataIndex: "poor",
            minWidth: 200,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "> 1.5",
          },
          details: {
            title: "Actions",
            dataIndex: "details",
            minWidth: 100,
            valueRenderingType: "details-link",
          },
        },
        tableColumnOrder: ["col1", "excellent", "good", "poor", "details"],
      };
    },

    getModalTitle: (dataPoint, startDate, endDate) => {
      const currentGroupBy = dataPoint?.groupBy?.label || "";
      const selectedLabels = getSelectedLabels(dataPoint?.selectedItems);
      const project = dataPoint?.projectName || "";
      const dateRange = formatDateRangeWithSmartYear(startDate, endDate);

      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            component="span"
            variant="fw500s16c8F8F8F"
            sx={{ lineHeight: "28px" }}
          >
            {`Fleet Energy Consumption per Km: ${currentGroupBy} / `}
            {selectedLabels}
            {` / `}
          </Typography>
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              lineHeight: "28px",
              fontWeight: 700,
              color: "#000",
              fontSize: "16px",
            }}
          >
            {project}
          </Typography>
          <Typography
            component="span"
            variant="fw400s16c8F8F8F"
            sx={{ mx: 0.5, lineHeight: "28px" }}
          >
            •
          </Typography>
          <Typography
            component="span"
            variant="fw500s16c8F8F8F"
            sx={{ lineHeight: "28px" }}
          >
            {dateRange}
          </Typography>
        </Box>
      );
    },

    level2Title: (record) => `${record.col1} / Details`,

    getTableData: async (dataPoint, pageNumber, rowsPerPage, sortBy, globalFilters) => {
      if (!dataPoint) return { data: [], total: 0 };

      const backendGroupBy =
        GROUP_BY_MAP[dataPoint.groupBy?.value] || "originCity";

      const filters = [];
      if (
        dataPoint.selectedItems?.length > 0 &&
        !dataPoint.selectedItems.some((i) => i.id === "all")
      ) {
        filters.push({
          name: backendGroupBy,
          values: dataPoint.selectedItems.map((i) => i.id),
        });
      }
      if (dataPoint.projectName) {
        filters.push({
          name: "ProjectName",
          values: [dataPoint.projectName],
        });
      }

      const apiParams = {
        groupBy: [backendGroupBy, "AverageEnergyConsumedBucket"],
        select: ["DistinctVehicleCount"],
        startDate: dataPoint.startDate,
        endDate: dataPoint.endDate,
        filters: filters.length > 0 ? filters : [],
        sortBy:
          sortBy && sortBy.length > 0 ? JSON.stringify(sortBy) : undefined,
        pageNum: pageNumber,
        pageCount: rowsPerPage,
        skipPagination: false,
      };

      const response = await getFleetPerformanceAnalyticsList(
        globalFilters,
        apiParams,
      );
      if (
        response &&
        response.responseStatus &&
        response.responseStatus.code === 200
      ) {
        const rawData = response.response?.data || [];
        const total =
          response.response?.paginationInfo?.total || rawData.length;
        const items = rawData.map((item) => {
          const metrics = item.metricsArray || [];
          const getValue = (bucket) =>
            metrics.find((m) => m.type === bucket)?.value || 0;
          return {
            id: item.xLabel,
            col1: item.xLabel,
            excellent: getValue("< 1.25"),
            good: getValue("1.25 - 1.5"),
            poor: getValue("> 1.5"),
          };
        });
        return { data: items, total: total };
      }
      return { data: [], total: 0 };
    },

    // applyTableAction for level 1 – mirrors getTableData transformation
    applyTableAction: async (
      rowSelectionKeys,
      actionType,
      selectAll,
      params,
      dataPoint,
      globalFilters,
      startDate,
      endDate,
    ) => {
      if (!startDate || !endDate) return actionType === "download" ? [] : null;

      const backendGroupBy =
        GROUP_BY_MAP[dataPoint.groupBy?.value] || "originCity";

      const filters = [];
      if (
        dataPoint.selectedItems?.length > 0 &&
        !dataPoint.selectedItems.some((i) => i.id === "all")
      ) {
        filters.push({
          name: backendGroupBy,
          values: dataPoint.selectedItems.map((i) => i.id),
        });
      }
      if (dataPoint.projectName) {
        filters.push({ name: "ProjectName", values: [dataPoint.projectName] });
      }

      const groupByLabelMap = {
        originCity: "City",
        originDepot: "Depot",
        ProjectName: "Project",
        originState: "State",
        mfgYear: "Year",
      };
      // The customer dimensions are named by org meta, so their CSV/mail header
      // comes from the clicked dimension's own label. Kept after the map so the
      // headers of the four original dimensions are unchanged.
      const groupByLabel =
        groupByLabelMap[backendGroupBy] ||
        dataPoint.groupBy?.label?.replace("By ", "") ||
        "Location";

      const apiParams = {
        actionType,
        groupBy: [backendGroupBy, "AverageEnergyConsumedBucket"],
        select: ["DistinctVehicleCount"],
        startDate: dataPoint.startDate,
        endDate: dataPoint.endDate,
        filters: filters.length > 0 ? filters : [],
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        // Use raw backend flat-data keys so convertDataForCSV maps headers correctly
        columns: [
          { dataIndex: "xLabel", headerLabel: groupByLabel },
          ...ENERGY_CONSUMPTION_BUCKETS.map((b) => ({
            dataIndex: b.backendValue,
            headerLabel: b.label,
          })),
        ],
        columnOrder: [
          groupByLabel,
          ...ENERGY_CONSUMPTION_BUCKETS.map((b) => b.label),
        ],
        ...(actionType === "mail" && {
          emailTo: params.emailTo,
          emailCc: params.emailCc,
          subject: params.subject,
          body: params.body,
        }),
      };

      const res = await getFleetPerformanceAnalyticsListActions(
        globalFilters,
        apiParams,
      );
      if (res && res.responseStatus?.code === 200) {
        return res.response?.data || (actionType === "download" ? [] : null);
      }
      return actionType === "download" ? [] : null;
    },

    level2GetTableData: async (
      level2Record,
      drillDownDataPoint,
      pageNumber,
      rowsPerPage,
      sortBy,
      globalFilters,
      selectedCustomFilters,
      options = {},
    ) => {
      if (!level2Record || !drillDownDataPoint)
        return { data: [], total: 0 };

      const backendGroupBy =
        GROUP_BY_MAP[drillDownDataPoint.groupBy?.value] || "originCity";

      const apiParams = {
        groupBy: ["batteryID"],
        select: ["AveragekWhPerKm", "MinkWhPerKm", "MaxkWhPerKm"],
        metaInfo: withConfiguredColumns(options, [
          "licencePlatNumber",
          "originCity",
          "originDepot",
          "originState",
          "deviceID",
          "pingStatus",
        ]),
        startDate: drillDownDataPoint.startDate,
        endDate: drillDownDataPoint.endDate,
        filters: [
          { name: "AverageEnergyConsumedBucket", values: [drillDownDataPoint.seriesName] },
          { name: "ProjectName", values: [drillDownDataPoint.projectName] },
          { name: backendGroupBy, values: [level2Record.col1] },
        ],
        sortBy:
          sortBy && sortBy.length > 0 ? JSON.stringify(sortBy) : undefined,
        pageNum: pageNumber,
        pageCount: rowsPerPage,
        skipPagination: false,
      };

      if (selectedCustomFilters && Object.keys(selectedCustomFilters).length > 0) {
        Object.entries(selectedCustomFilters).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            apiParams.filters.push({ name: key, values: value });
          } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
            apiParams.filters.push({ name: key, min: value.min, max: value.max });
          }
        });
      }

      const res = await getTableDataFromApi(globalFilters, apiParams);
      return res;
    },

    // level2ApplyTableAction – mirrors level2GetTableData
    level2ApplyTableAction: async (
      rowSelectionKeys,
      actionType,
      selectAll,
      params,
      level2Record,
      drillDownDataPoint,
      globalFilters,
      startDate,
      endDate,
      options = {},
    ) => {
      if (!level2Record || !drillDownDataPoint || !startDate || !endDate)
        return actionType === "download" ? [] : null;

      const backendGroupBy =
        GROUP_BY_MAP[drillDownDataPoint.groupBy?.value] || "originCity";

      const apiParams = {
        actionType,
        groupBy: ["batteryID"],
        select: ["AveragekWhPerKm", "MinkWhPerKm", "MaxkWhPerKm"],
        metaInfo: withConfiguredColumns(options, [
          "licencePlatNumber",
          "originCity",
          "originDepot",
          "originState",
        ]),
        startDate: drillDownDataPoint.startDate,
        endDate: drillDownDataPoint.endDate,
        filters: [
          { name: "AverageEnergyConsumedBucket", values: [drillDownDataPoint.seriesName] },
          { name: "ProjectName", values: [drillDownDataPoint.projectName] },
          { name: backendGroupBy, values: [level2Record.col1] },
        ],
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        columns: [
          { dataIndex: "licencePlatNumber", headerLabel: "Bus Number" },
          {
            dataIndex: "AveragekWhPerKm",
            headerLabel: "Avg. Energy Consumed per Km (kWh/km)",
          },
          {
            dataIndex: "Bounds of Energy Consumed per Km (kWh/km)",
            headerLabel: "Bounds of Energy Consumed per Km (kWh/km)",
          },
          {
            dataIndex: "MinkWhPerKm",
            headerLabel: "Min. Energy Consumed per Km (kWh/km)",
          },
          {
            dataIndex: "MaxkWhPerKm",
            headerLabel: "Max. Energy Consumed per Km (kWh/km)",
          },
          { dataIndex: "originCity", headerLabel: "City" },
          { dataIndex: "originDepot", headerLabel: "Depot" },
          { dataIndex: "originState", headerLabel: "State" },
        ],
        columnOrder: [
          "Bus Number",
          "Avg. Energy Consumed per Km (kWh/km)",
          "Bounds of Energy Consumed per Km (kWh/km)",
          "Min. Energy Consumed per Km (kWh/km)",
          "Max. Energy Consumed per Km (kWh/km)",
          "Depot",
          "City",
          "State",
        ],
        ...(actionType === "mail" && {
          emailTo: params.emailTo,
          emailCc: params.emailCc,
          subject: params.subject,
          body: params.body,
        }),
      };

      if (params?.selectedFilters && Object.keys(params.selectedFilters).length > 0) {
        Object.entries(params.selectedFilters).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            apiParams.filters.push({ name: key, values: value });
          } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
            apiParams.filters.push({ name: key, min: value.min, max: value.max });
          }
        });
      }

      const res = await getFleetPerformanceAnalyticsListActions(
        globalFilters,
        apiParams,
      );
      if (res && res.responseStatus?.code === 200) {
        return res.response?.data || (actionType === "download" ? [] : null);
      }
      return actionType === "download" ? [] : null;
    },

    level2Title: (record) => (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        <Typography
          component="span"
          sx={{ color: "#8F8F8F", mr: 0.5, fontSize: "14px", fontWeight: 500 }}
        >
          {record?.col1}
        </Typography>
        <Typography
          component="span"
          sx={{ color: "#8F8F8F", mr: 0.5, fontSize: "14px", fontWeight: 500 }}
        >
          /
        </Typography>
        <Typography
          component="span"
          sx={{ color: "#000000", fontSize: "14px", fontWeight: 500 }}
        >
          Details
        </Typography>
      </Box>
    ),
  },

  ENERGY_CONSUMPTION_TRENDS: {
    getModalTitle: (dataPoint, startDate, endDate) => {
      const timeLabel = dataPoint?.xLabel || "Selected Period";
      const seriesName = dataPoint?.seriesName || "";

      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            component="span"
            variant="fw500s16c8F8F8F"
            sx={{ lineHeight: "28px" }}
          >
            {`Energy Consumption Trend Details: ${timeLabel} / `}
          </Typography>
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              lineHeight: "28px",
              fontWeight: 700,
              color: "#000",
              fontSize: "16px",
            }}
          >
            {seriesName}
          </Typography>
        </Box>
      );
    },

    orgMetaTableKeys: { 2: "energyConsumptionTrendsVehiclesTable" },
    getTableConfig: (level, dataPoint) => {
      const seriesName = dataPoint?.seriesName || "Category";
      if (level === 2) {
        return {
          title: "Vehicles Affected",
          disableFiltersSettings: false,
          disableColumnSettings: false,
          tableColumnsConfig: {
            licencePlatNumber: {
              title: "Bus Number",
              dataIndex: "licencePlatNumber",
              minWidth: 150,
              valueRenderingType: "device-id-link",
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
              fixed: "left",
            },
            AveragekWhPerKm: {
              title: "Avg. Energy Consumed per Km (kWh/km)",
              dataIndex: "AveragekWhPerKm",
              valueRenderingType: "number",
              decimalPlaces: 2,
              minWidth: 200,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            bounds: {
              title: "Bounds of Energy Consumed per Km (kWh/km)",
              dataIndex: "Bounds of Energy Consumed per Km (kWh/km)",
              valueRenderingType: "number",
              minWidth: 250,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
              sortKey: "AveragekWhPerKm",
            },
            originDepot: {
              title: "Depot",
              dataIndex: "originDepot",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originCity: {
              title: "City",
              dataIndex: "originCity",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originState: {
              title: "State",
              dataIndex: "originState",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
          },
          tableColumnOrder: [
            "licencePlatNumber",
            "AveragekWhPerKm",
            "bounds",
            "originDepot",
            "originCity",
            "originState",
          ],
          filtersConfig: [
            { key: "originState", type: "dynamic", label: "State", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originState" } },
            { key: "originCity", type: "dynamic", label: "City", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originCity" } },
            { key: "originDepot", type: "dynamic", label: "Depot", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originDepo" } },
            { key: "AveragekWhPerKm", type: "static", label: "Energy Consumption", staticData: [], displayType: "range", unitOfMeasurement: "kWh/km" },
          ],
        };
      }

      return {
        title: `${seriesName} Consumption Efficiency`,
        tableColumnsConfig: {
          xLabel: {
            title: "Project",
            dataIndex: "xLabel",
            minWidth: 200,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "ProjectName"
          },
          AveragekWhPerKm: {
            title: "Avg. Energy Consumed per Km (kWh/km)",
            dataIndex: "AveragekWhPerKm",
            valueRenderingType: "number",
            decimalPlaces: 2,
            minWidth: 200,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          DistinctVehicleCount: {
            title: "No. of Vehicles",
            dataIndex: "DistinctVehicleCount",
            minWidth: 150,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "DistinctVehicleCount",
          },
          details: {
            title: "Actions",
            dataIndex: "details",
            minWidth: 100,
            valueRenderingType: "details-link",
          },
        },
        tableColumnOrder: ["xLabel", "AveragekWhPerKm", "DistinctVehicleCount", "details"],
      };
    },

    level2Title: (record) => (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        <Typography
          component="span"
          sx={{ color: "#8F8F8F", mr: 0.5, fontSize: "14px", fontWeight: 500 }}
        >
          {record?.xLabel}
        </Typography>
        <Typography
          component="span"
          sx={{ color: "#8F8F8F", mr: 0.5, fontSize: "14px", fontWeight: 500 }}
        >
          /
        </Typography>
        <Typography
          component="span"
          sx={{ color: "#000000", fontSize: "14px", fontWeight: 500 }}
        >
          Details
        </Typography>
      </Box>
    ),

    getTableData: async (dataPoint, pageNumber, rowsPerPage, sortBy, globalFilters) => {
      if (!dataPoint) return { data: [], total: 0 };

      const bucket = bucketLabelToBackend[dataPoint.seriesName] || dataPoint.seriesName;

      const apiParams = {
        groupBy: ["ProjectName"],
        select: ["AveragekWhPerKm", "DistinctVehicleCount"],
        startDate: dataPoint.startDate,
        endDate: dataPoint.endDate,
        filters: [{ name: "AverageEnergyConsumedBucket", values: [bucket] }],
        sortBy:
          sortBy && sortBy.length > 0 ? JSON.stringify(sortBy) : undefined,
        pageNum: pageNumber,
        pageCount: rowsPerPage,
        skipPagination: false,
      };

      const res = await getTableDataFromApi(globalFilters, apiParams);
      return res;
    },

    applyTableAction: async (
      rowSelectionKeys,
      actionType,
      selectAll,
      params,
      dataPoint,
      globalFilters,
    ) => {
      if (!dataPoint) return actionType === "download" ? [] : null;

      const bucket = bucketLabelToBackend[dataPoint.seriesName] || dataPoint.seriesName;

      const apiParams = {
        actionType,
        groupBy: ["ProjectName"],
        select: ["AveragekWhPerKm", "DistinctVehicleCount"],
        startDate: dataPoint.startDate,
        endDate: dataPoint.endDate,
        filters: [{ name: "AverageEnergyConsumedBucket", values: [bucket] }],
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        columns: [
          { dataIndex: "xLabel", headerLabel: "Project" },
          {
            dataIndex: "AveragekWhPerKm",
            headerLabel: "Avg. Energy Consumed per Km (kWh/km)",
          },
          { dataIndex: "DistinctVehicleCount", headerLabel: "No. of Vehicles" },
        ],
        columnOrder: [
          "Project",
          "Avg. Energy Consumed per Km (kWh/km)",
          "No. of Vehicles",
        ],
        ...(actionType === "mail" && {
          emailTo: params.emailTo,
          emailCc: params.emailCc,
          subject: params.subject,
          body: params.body,
        }),
      };

      const res = await getFleetPerformanceAnalyticsListActions(
        globalFilters,
        apiParams,
      );
      return res?.responseStatus?.code === 200
        ? res.response?.data || (actionType === "download" ? [] : null)
        : actionType === "download"
          ? []
          : null;
    },

    level2GetTableData: async (
      level2Record,
      drillDownDataPoint,
      pageNumber,
      rowsPerPage,
      sortBy,
      globalFilters,
      selectedCustomFilters,
      options = {},
    ) => {
      if (!level2Record || !drillDownDataPoint) return { data: [], total: 0 };

      const bucket =
        bucketLabelToBackend[drillDownDataPoint.seriesName] ||
        drillDownDataPoint.seriesName;

      const apiParams = {
        groupBy: ["batteryID"],
        select: ["AveragekWhPerKm", "MinkWhPerKm", "MaxkWhPerKm"],
        metaInfo: withConfiguredColumns(options, [
          "licencePlatNumber",
          "originCity",
          "originDepot",
          "originState",
          "deviceID",
          "pingStatus",
        ]),
        startDate: drillDownDataPoint.startDate,
        endDate: drillDownDataPoint.endDate,
        filters: [
          { name: "AverageEnergyConsumedBucket", values: [bucket] },
          { name: "ProjectName", values: [level2Record.xLabel] },
        ],
        sortBy:
          sortBy && sortBy.length > 0 ? JSON.stringify(sortBy) : undefined,
        pageNum: pageNumber,
        pageCount: rowsPerPage,
        skipPagination: false,
      };

      if (selectedCustomFilters && Object.keys(selectedCustomFilters).length > 0) {
        Object.entries(selectedCustomFilters).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            apiParams.filters.push({ name: key, values: value });
          } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
            apiParams.filters.push({ name: key, min: value.min, max: value.max });
          }
        });
      }

      const res = await getTableDataFromApi(globalFilters, apiParams);
      return res;
    },

    level2ApplyTableAction: async (
      rowSelectionKeys,
      actionType,
      selectAll,
      params,
      level2Record,
      drillDownDataPoint,
      globalFilters,
      startDate,
      endDate,
      options = {},
    ) => {
      if (!level2Record || !drillDownDataPoint)
        return actionType === "download" ? [] : null;

      const bucket =
        bucketLabelToBackend[drillDownDataPoint.seriesName] ||
        drillDownDataPoint.seriesName;

      const apiParams = {
        actionType,
        groupBy: ["batteryID"],
        select: ["AveragekWhPerKm", "MinkWhPerKm", "MaxkWhPerKm"],
        metaInfo: withConfiguredColumns(options, [
          "licencePlatNumber",
          "originCity",
          "originDepot",
          "originState",
        ]),
        startDate: drillDownDataPoint.startDate,
        endDate: drillDownDataPoint.endDate,
        filters: [
          { name: "AverageEnergyConsumedBucket", values: [bucket] },
          { name: "ProjectName", values: [level2Record.xLabel] },
        ],
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        columns: [
          { dataIndex: "licencePlatNumber", headerLabel: "Bus Number" },
          {
            dataIndex: "AveragekWhPerKm",
            headerLabel: "Avg. Energy Consumed per Km (kWh/km)",
          },
          {
            dataIndex: "Bounds of Energy Consumed per Km (kWh/km)",
            headerLabel: "Bounds of Energy Consumed per Km (kWh/km)",
          },
          {
            dataIndex: "MinkWhPerKm",
            headerLabel: "Min. Energy Consumed per Km (kWh/km)",
          },
          {
            dataIndex: "MaxkWhPerKm",
            headerLabel: "Max. Energy Consumed per Km (kWh/km)",
          },
          { dataIndex: "originDepot", headerLabel: "Depot" },
          { dataIndex: "originCity", headerLabel: "City" },
          { dataIndex: "originState", headerLabel: "State" },
        ],
        columnOrder: [
          "Bus Number",
          "Avg. Energy Consumed per Km (kWh/km)",
          "Bounds of Energy Consumed per Km (kWh/km)",
          "Min. Energy Consumed per Km (kWh/km)",
          "Max. Energy Consumed per Km (kWh/km)",
          "Depot",
          "City",
          "State",
        ],
        ...(actionType === "mail" && {
          emailTo: params.emailTo,
          emailCc: params.emailCc,
          subject: params.subject,
          body: params.body,
        }),
      };

      if (params?.selectedFilters && Object.keys(params.selectedFilters).length > 0) {
        Object.entries(params.selectedFilters).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            apiParams.filters.push({ name: key, values: value });
          } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
            apiParams.filters.push({ name: key, min: value.min, max: value.max });
          }
        });
      }

      const res = await getFleetPerformanceAnalyticsListActions(
        globalFilters,
        apiParams,
      );
      return res?.responseStatus?.code === 200
        ? res.response?.data || (actionType === "download" ? [] : null)
        : actionType === "download"
          ? []
          : null;
    },
  },

  ENERGY_FLOW_BREAKDOWN: {
    orgMetaTableKeys: { 2: "energyFlowBreakdownVehiclesTable" },
    getTableConfig: (level, dataPoint) => {
      if (level === 2) {
        return {
          title: "Vehicles Affected",
          disableFiltersSettings: false,
          disableColumnSettings: false,
          tableColumnsConfig: {
            licencePlatNumber: {
              title: "Bus Number",
              dataIndex: "licencePlatNumber",
              minWidth: 150,
              valueRenderingType: "device-id-link",
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
              fixed: "left",
            },
            ChargeEnergyConsumedTotal: {
              title: "Charge Energy Consumption (kWh)",
              dataIndex: "ChargeEnergyConsumedTotal",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            DischargeEnergyConsumedTotal: {
              title: "Discharge Energy Consumption (kWh)",
              dataIndex: "DischargeEnergyConsumedTotal",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            RegenEnergyTotal: {
              title: "Regeneration Energy (kWh)",
              dataIndex: "RegenEnergyTotal",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originCity: {
              title: "City",
              dataIndex: "originCity",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originDepot: {
              title: "Depot",
              dataIndex: "originDepot",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originState: {
              title: "State",
              dataIndex: "originState",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
          },
          tableColumnOrder: [
            "licencePlatNumber",
            "ChargeEnergyConsumedTotal",
            "DischargeEnergyConsumedTotal",
            "RegenEnergyTotal",
            "originDepot",
            "originCity",
            "originState",
          ],
          filtersConfig: [
            { key: "originState", type: "dynamic", label: "State", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originState" } },
            { key: "originCity", type: "dynamic", label: "City", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originCity" } },
            { key: "originDepot", type: "dynamic", label: "Depot", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originDepo" } },
            { key: "ChargeEnergyConsumedTotal", type: "static", label: "Charge Energy Consumption", staticData: [], displayType: "range", unitOfMeasurement: "kWh" },
          ],
        };
      }
      const groupByLabel =
        dataPoint?.groupBy?.label?.replace("By ", "") || "City";
      const selectedLabels = getSelectedLabels(dataPoint?.selectedItems);
      const project = dataPoint?.projectName || "";

      return {
        title: (
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            <Typography
              component="span"
              variant="fw700s18lh24c000000"
              sx={{ mr: 0.5, color: "#000" }}
            >
              {selectedLabels}:
            </Typography>
            <Typography
              component="span"
              variant="fw700s18lh24c000000"
              sx={{ color: "#000" }}
            >
              {project}
            </Typography>
          </Box>
        ),
        tableColumnsConfig: {
          col1: {
            title: groupByLabel,
            dataIndex: "col1",
            minWidth: 150,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: GROUP_BY_MAP[dataPoint?.groupBy?.value] || "originCity",
          },
          charge: {
            title: "Charge Energy Consumption (kWh)",
            dataIndex: "charge",
            minWidth: 150,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "ChargeEnergyConsumedTotal",
          },
          discharge: {
            title: "Discharge Energy Consumption (kWh)",
            dataIndex: "discharge",
            minWidth: 150,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "DischargeEnergyConsumedTotal",
          },
          regen: {
            title: "Regeneration Energy (kWh)",
            dataIndex: "regen",
            minWidth: 150,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "RegenEnergyTotal",
          },
          details: {
            title: "Actions",
            dataIndex: "details",
            minWidth: 100,
            valueRenderingType: "details-link",
          },
        },
        tableColumnOrder: ["col1", "charge", "discharge", "regen", "details"],
      };
    },

    getModalTitle: (dataPoint, startDate, endDate) => {
      const currentGroupBy = dataPoint?.groupBy?.label || "By City";
      const selectedLabels = getSelectedLabels(dataPoint?.selectedItems);
      const project = dataPoint?.projectName || "";
      const dateRange = formatDateRangeWithSmartYear(startDate, endDate);

      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            component="span"
            variant="fw500s16c8F8F8F"
            sx={{ lineHeight: "28px" }}
          >
            {`Energy Flow Breakdown Analysis: ${currentGroupBy} / `}
            {selectedLabels}
            {` / `}
          </Typography>
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              lineHeight: "28px",
              fontWeight: 700,
              color: "#000",
              fontSize: "16px",
            }}
          >
            {project}
          </Typography>
          <Typography
            component="span"
            variant="fw400s16c8F8F8F"
            sx={{ mx: 0.5, lineHeight: "28px" }}
          >
            •
          </Typography>
          <Typography
            component="span"
            variant="fw500s16c8F8F8F"
            sx={{ lineHeight: "28px" }}
          >
            {dateRange}
          </Typography>
        </Box>
      );
    },

    level2Title: (record) => (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        <Typography
          component="span"
          sx={{ color: "#8F8F8F", mr: 0.5, fontSize: "14px", fontWeight: 500 }}
        >
          {record?.col1}
        </Typography>
        <Typography
          component="span"
          sx={{ color: "#8F8F8F", mr: 0.5, fontSize: "14px", fontWeight: 500 }}
        >
          /
        </Typography>
        <Typography
          component="span"
          sx={{ color: "#000000", fontSize: "14px", fontWeight: 500 }}
        >
          Details
        </Typography>
      </Box>
    ),

    getTableData: async (dataPoint, pageNumber, rowsPerPage, sortBy, globalFilters) => {
      if (!dataPoint) return { data: [], total: 0 };

      const backendGroupBy =
        GROUP_BY_MAP[dataPoint.groupBy?.value] || "originCity";

      const apiParams = {
        groupBy: [backendGroupBy],
        select: [
          "ChargeEnergyConsumedTotal",
          "DischargeEnergyConsumedTotal",
          "RegenEnergyTotal",
        ],
        startDate: dataPoint.startDate,
        endDate: dataPoint.endDate,
        filters: dataPoint.projectName
          ? [{ name: "ProjectName", values: [dataPoint.projectName] }]
          : [],
        sortBy:
          sortBy && sortBy.length > 0 ? JSON.stringify(sortBy) : undefined,
        pageNum: pageNumber,
        pageCount: rowsPerPage,
        skipPagination: false,
      };

      const response = await getFleetPerformanceAnalyticsList(
        globalFilters,
        apiParams,
      );
      if (response?.responseStatus?.code === 200) {
        const rawData = response.response?.data || [];
        const total =
          response.response?.paginationInfo?.total || rawData.length;
        const items = rawData.map((item) => {
          const getMetric = (type) =>
            item.metricsArray?.find((m) => m.type === type)?.value || 0;
          return {
            id: item.xLabel,
            col1: item.xLabel,
            charge: getMetric("ChargeEnergyConsumedTotal"),
            discharge: getMetric("DischargeEnergyConsumedTotal"),
            regen: getMetric("RegenEnergyTotal"),
          };
        });
        return { data: items, total };
      }
      return { data: [], total: 0 };
    },

    applyTableAction: async (
      rowSelectionKeys,
      actionType,
      selectAll,
      params,
      dataPoint,
      globalFilters,
      startDate,
      endDate,
    ) => {
      if (!startDate || !endDate) return actionType === "download" ? [] : null;

      const backendGroupBy =
        GROUP_BY_MAP[dataPoint.groupBy?.value] || "originCity";
      const groupByLabelMap = {
        originCity: "City",
        originDepot: "Depot",
        ProjectName: "Project",
        originState: "State",
        mfgYear: "Year",
      };
      // The customer dimensions are named by org meta, so their CSV/mail header
      // comes from the clicked dimension's own label. Kept after the map so the
      // headers of the four original dimensions are unchanged.
      const groupByLabel =
        groupByLabelMap[backendGroupBy] ||
        dataPoint.groupBy?.label?.replace("By ", "") ||
        "Location";

      const apiParams = {
        actionType,
        groupBy: [backendGroupBy],
        select: [
          "ChargeEnergyConsumedTotal",
          "DischargeEnergyConsumedTotal",
          "RegenEnergyTotal",
        ],
        startDate: dataPoint.startDate,
        endDate: dataPoint.endDate,
        filters: dataPoint.projectName
          ? [{ name: "ProjectName", values: [dataPoint.projectName] }]
          : [],
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        columns: [
          { dataIndex: "xLabel", headerLabel: groupByLabel },
          {
            dataIndex: "ChargeEnergyConsumedTotal",
            headerLabel: "Charge Energy Consumption (kWh)",
          },
          {
            dataIndex: "DischargeEnergyConsumedTotal",
            headerLabel: "Discharge Energy Consumption (kWh)",
          },
          { dataIndex: "RegenEnergyTotal", headerLabel: "Regeneration Energy (kWh)" },
        ],
        columnOrder: [
          groupByLabel,
          "Charge Energy Consumption (kWh)",
          "Discharge Energy Consumption (kWh)",
          "Regeneration Energy (kWh)",
        ],
        ...(actionType === "mail" && {
          emailTo: params.emailTo,
          emailCc: params.emailCc,
          subject: params.subject,
          body: params.body,
        }),
      };

      const res = await getFleetPerformanceAnalyticsListActions(
        globalFilters,
        apiParams,
      );
      return res?.responseStatus?.code === 200
        ? res.response?.data || (actionType === "download" ? [] : null)
        : actionType === "download"
          ? []
          : null;
    },

    level2GetTableData: async (
      level2Record,
      drillDownDataPoint,
      pageNumber,
      rowsPerPage,
      sortBy,
      globalFilters,
      selectedCustomFilters,
      options = {},
    ) => {
      if (!level2Record || !drillDownDataPoint) return { data: [], total: 0 };

      const backendGroupBy =
        GROUP_BY_MAP[drillDownDataPoint.groupBy?.value] || "originCity";

      const apiParams = {
        groupBy: ["batteryID"],
        select: [
          "ChargeEnergyConsumedTotal",
          "DischargeEnergyConsumedTotal",
          "RegenEnergyTotal",
          "DistanceTotal",
        ],
        metaInfo: withConfiguredColumns(options, [
          "licencePlatNumber",
          "originCity",
          "originDepot",
          "originState",
          "deviceID",
          "pingStatus",
        ]),
        startDate: drillDownDataPoint.startDate,
        endDate: drillDownDataPoint.endDate,
        filters: [
          { name: "ProjectName", values: [drillDownDataPoint.projectName] },
          { name: backendGroupBy, values: [level2Record.col1] },
        ],
        sortBy:
          sortBy && sortBy.length > 0 ? JSON.stringify(sortBy) : undefined,
        pageNum: pageNumber,
        pageCount: rowsPerPage,
        skipPagination: false,
      };

      if (selectedCustomFilters && Object.keys(selectedCustomFilters).length > 0) {
        Object.entries(selectedCustomFilters).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            apiParams.filters.push({ name: key, values: value });
          } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
            apiParams.filters.push({ name: key, min: value.min, max: value.max });
          }
        });
      }

      const response = await getFleetPerformanceAnalyticsList(
        globalFilters,
        apiParams,
      );
      if (response?.responseStatus?.code === 200) {
        const rawData = response.response?.data || [];
        const total =
          response.response?.paginationInfo?.total || rawData.length;
        const items = rawData.map((item) => {
          const getMetric = (type) =>
            item.metricsArray?.find((m) => m.type === type)?.value || 0;
          return {
            id: item.xLabel,
            ...item.metaInfo,
            ChargeEnergyConsumedTotal: getMetric("ChargeEnergyConsumedTotal"),
            DischargeEnergyConsumedTotal: getMetric(
              "DischargeEnergyConsumedTotal",
            ),
            RegenEnergyTotal: getMetric("RegenEnergyTotal"),
          };
        });
        return { data: items, total };
      }
      return { data: [], total: 0 };
    },

    level2ApplyTableAction: async (
      rowSelectionKeys,
      actionType,
      selectAll,
      params,
      level2Record,
      drillDownDataPoint,
      globalFilters,
      startDate,
      endDate,
      options = {},
    ) => {
      if (!level2Record || !drillDownDataPoint || !startDate || !endDate)
        return actionType === "download" ? [] : null;

      const backendGroupBy =
        GROUP_BY_MAP[drillDownDataPoint.groupBy?.value] || "originCity";

      const apiParams = {
        actionType,
        groupBy: ["batteryID"],
        select: [
          "ChargeEnergyConsumedTotal",
          "DischargeEnergyConsumedTotal",
          "RegenEnergyTotal",
        ],
        metaInfo: withConfiguredColumns(options, [
          "licencePlatNumber",
          "originCity",
          "originDepot",
          "originState",
        ]),
        startDate: drillDownDataPoint.startDate,
        endDate: drillDownDataPoint.endDate,
        filters: [
          { name: "ProjectName", values: [drillDownDataPoint.projectName] },
          { name: backendGroupBy, values: [level2Record.col1] },
        ],
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        columns: [
          { dataIndex: "licencePlatNumber", headerLabel: "Bus Number" },
          {
            dataIndex: "ChargeEnergyConsumedTotal",
            headerLabel: "Charge Energy Consumption (kWh)",
          },
          {
            dataIndex: "DischargeEnergyConsumedTotal",
            headerLabel: "Discharge Energy Consumption (kWh)",
          },
          { dataIndex: "RegenEnergyTotal", headerLabel: "Regeneration Energy (kWh)" },
          { dataIndex: "originCity", headerLabel: "City" },
          { dataIndex: "originDepot", headerLabel: "Depot" },
          { dataIndex: "originState", headerLabel: "State" },
        ],
        columnOrder: [
          "Bus Number",
          "Charge Energy Consumption (kWh)",
          "Discharge Energy Consumption (kWh)",
          "Regeneration Energy (kWh)",
          "Depot",
          "City",
          "State",
        ],
        ...(actionType === "mail" && {
          emailTo: params.emailTo,
          emailCc: params.emailCc,
          subject: params.subject,
          body: params.body,
        }),
      };

      if (params?.selectedFilters && Object.keys(params.selectedFilters).length > 0) {
        Object.entries(params.selectedFilters).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            apiParams.filters.push({ name: key, values: value });
          } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
            apiParams.filters.push({ name: key, min: value.min, max: value.max });
          }
        });
      }

      const res = await getFleetPerformanceAnalyticsListActions(
        globalFilters,
        apiParams,
      );
      return res?.responseStatus?.code === 200
        ? res.response?.data || (actionType === "download" ? [] : null)
        : actionType === "download"
          ? []
          : null;
    },
  },

  ENERGY_FLOW_TRENDS: {
    getModalTitle: (dataPoint) => {
      const timeLabel = dataPoint?.xLabel || "Selected Period";
      const seriesName = dataPoint?.seriesName || "";

      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            component="span"
            variant="fw500s16c8F8F8F"
            sx={{ lineHeight: "28px" }}
          >
            {`Energy Flow Trend Details: ${timeLabel} / `}
          </Typography>
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              lineHeight: "28px",
              fontWeight: 700,
              color: "#000",
              fontSize: "16px",
            }}
          >
            {seriesName}
          </Typography>
        </Box>
      );
    },

    orgMetaTableKeys: { 2: "energyFlowTrendsVehiclesTable" },
    getTableConfig: (level, dataPoint) => {
      const seriesName = dataPoint?.seriesName || "Category";
      if (level === 2) {
        return {
          title: "Vehicles Affected",
          disableFiltersSettings: false,
          disableColumnSettings: false,
          tableColumnsConfig: {
            licencePlatNumber: {
              title: "Bus Number",
              dataIndex: "licencePlatNumber",
              minWidth: 150,
              valueRenderingType: "device-id-link",
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
              fixed: "left",
            },
            ChargeEnergyConsumedTotal: {
              title: "Charge Energy Consumption (kWh)",
              dataIndex: "ChargeEnergyConsumedTotal",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            DischargeEnergyConsumedTotal: {
              title: "Discharge Energy Consumption (kWh)",
              dataIndex: "DischargeEnergyConsumedTotal",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            RegenEnergyTotal: {
              title: "Regeneration Energy (kWh)",
              dataIndex: "RegenEnergyTotal",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            DistanceTotal: {
              title: "Distance Covered (km)",
              dataIndex: "DistanceTotal",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originDepot: {
              title: "Depot",
              dataIndex: "originDepot",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originCity: {
              title: "City",
              dataIndex: "originCity",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
            originState: {
              title: "State",
              dataIndex: "originState",
              minWidth: 120,
              sorter: true,
              sortDirections: ["ascend", "descend", "ascend"],
            },
          },
          tableColumnOrder: [
            "licencePlatNumber",
            seriesName.includes("Charge") ? "ChargeEnergyConsumedTotal" : null,
            seriesName.includes("Discharge") ? "DischargeEnergyConsumedTotal" : null,
            seriesName.includes("Regen") ? "RegenEnergyTotal" : null,
            seriesName.includes("Distance") ? "DistanceTotal" : null,
            "originDepot",
            "originCity",
            "originState",
          ].filter(Boolean),
          filtersConfig: [
            { key: "originState", type: "dynamic", label: "State", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originState" } },
            { key: "originCity", type: "dynamic", label: "City", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originCity" } },
            { key: "originDepot", type: "dynamic", label: "Depot", staticData: [], displayType: "multiSelectDropdown", queryParams: { table: "vehicleConfig", column: "originDepo" } },
            seriesName.includes("Charge") ? { key: "ChargeEnergyConsumedTotal", type: "static", label: "Charge Energy Consumption (kWh)", staticData: [], displayType: "range", unitOfMeasurement: "kWh" } : null,
            seriesName.includes("Discharge") ? { key: "DischargeEnergyConsumedTotal", type: "static", label: "Discharge Energy Consumption (kWh)", staticData: [], displayType: "range", unitOfMeasurement: "kWh" } : null,
            seriesName.includes("Regen") ? { key: "RegenEnergyTotal", type: "static", label: "Regeneration Energy (kWh)", staticData: [], displayType: "range", unitOfMeasurement: "kWh" } : null,
            seriesName.includes("Distance") ? { key: "DistanceTotal", type: "static", label: "Distance Covered", staticData: [], displayType: "range", unitOfMeasurement: "km" } : null,
          ].filter(Boolean),
        };
      }

      return {
        title: `${seriesName} Performance`,
        tableColumnsConfig: {
          col1: {
            title: "Project",
            dataIndex: "col1",
            minWidth: 200,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "ProjectName",
          },
          charge: {
            title: "Charge Energy Consumption (kWh)",
            dataIndex: "charge",
            minWidth: 120,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "ChargeEnergyConsumedTotal",
          },
          discharge: {
            title: "Discharge Energy Consumption (kWh)",
            dataIndex: "discharge",
            minWidth: 120,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "DischargeEnergyConsumedTotal",
          },
          regen: {
            title: "Regeneration Energy (kWh)",
            dataIndex: "regen",
            minWidth: 120,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "RegenEnergyTotal",
          },
          distance: {
            title: "Distance Covered (km)",
            dataIndex: "distance",
            minWidth: 120,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "DistanceTotal",
          },
          DistinctVehicleCount: {
            title: "No. of Vehicles",
            dataIndex: "DistinctVehicleCount",
            minWidth: 150,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "DistinctVehicleCount",
          },
          details: {
            title: "Actions",
            dataIndex: "details",
            minWidth: 100,
            valueRenderingType: "details-link",
          },
        },
        tableColumnOrder: [
          "col1",
          seriesName.includes("Charge") ? "charge" : null,
          seriesName.includes("Discharge") ? "discharge" : null,
          seriesName.includes("Regen") ? "regen" : null,
          seriesName.includes("Distance") ? "distance" : null,
          "DistinctVehicleCount",
          "details",
        ].filter(Boolean),
      };
    },

    level2Title: (record) => (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        <Typography
          component="span"
          sx={{ color: "#8F8F8F", mr: 0.5, fontSize: "14px", fontWeight: 500 }}
        >
          {record?.col1}
        </Typography>
        <Typography
          component="span"
          sx={{ color: "#8F8F8F", mr: 0.5, fontSize: "14px", fontWeight: 500 }}
        >
          /
        </Typography>
        <Typography
          component="span"
          sx={{ color: "#000000", fontSize: "14px", fontWeight: 500 }}
        >
          Details
        </Typography>
      </Box>
    ),

    getTableData: async (dataPoint, pageNumber, rowsPerPage, sortBy, globalFilters) => {
      if (!dataPoint) return { data: [], total: 0 };

      const apiParams = {
        groupBy: ["ProjectName"],
        select: [
          "ChargeEnergyConsumedTotal",
          "DischargeEnergyConsumedTotal",
          "RegenEnergyTotal",
          "DistanceTotal",
          "DistinctVehicleCount",
        ],
        startDate: dataPoint.startDate,
        endDate: dataPoint.endDate,
        filters: [],
        sortBy:
          sortBy && sortBy.length > 0 ? JSON.stringify(sortBy) : undefined,
        pageNum: pageNumber,
        pageCount: rowsPerPage,
        skipPagination: false,
      };

      const response = await getFleetPerformanceAnalyticsList(
        globalFilters,
        apiParams,
      );
      if (response?.responseStatus?.code === 200) {
        const rawData = response.response?.data || [];
        const total =
          response.response?.paginationInfo?.total || rawData.length;
        const items = rawData.map((item) => {
          const getMetric = (type) =>
            item.metricsArray?.find((m) => m.type === type)?.value || 0;
          return {
            id: item.xLabel,
            col1: item.xLabel,
            charge: getMetric("ChargeEnergyConsumedTotal"),
            discharge: getMetric("DischargeEnergyConsumedTotal"),
            regen: getMetric("RegenEnergyTotal"),
            distance: getMetric("DistanceTotal"),
            DistinctVehicleCount: getMetric("DistinctVehicleCount"),
          };
        });
        return { data: items, total };
      }
      return { data: [], total: 0 };
    },

    applyTableAction: async (
      rowSelectionKeys,
      actionType,
      selectAll,
      params,
      dataPoint,
      globalFilters,
      startDate,
      endDate,
    ) => {
      if (!dataPoint) return actionType === "download" ? [] : null;

      const apiParams = {
        actionType,
        groupBy: ["ProjectName"],
        select: [
          "ChargeEnergyConsumedTotal",
          "DischargeEnergyConsumedTotal",
          "RegenEnergyTotal",
          "DistanceTotal",
          "DistinctVehicleCount",
        ],
        startDate: dataPoint.startDate,
        endDate: dataPoint.endDate,
        filters: [],
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        columns: [
          { dataIndex: "xLabel", headerLabel: "Project" },
          {
            dataIndex: "ChargeEnergyConsumedTotal",
            headerLabel: "Charge Energy Consumption (kWh)",
          },
          {
            dataIndex: "DischargeEnergyConsumedTotal",
            headerLabel: "Discharge Energy Consumption (kWh)",
          },
          { dataIndex: "RegenEnergyTotal", headerLabel: "Regeneration Energy (kWh)" },
          { dataIndex: "DistanceTotal", headerLabel: "Distance Covered (km)" },
          { dataIndex: "DistinctVehicleCount", headerLabel: "No. of Vehicles" },
        ],
        columnOrder: [
          "Project",
          "Charge Energy Consumption (kWh)",
          "Discharge Energy Consumption (kWh)",
          "Regeneration Energy (kWh)",
          "Distance Covered (km)",
          "No. of Vehicles",
        ],
        ...(actionType === "mail" && {
          emailTo: params.emailTo,
          emailCc: params.emailCc,
          subject: params.subject,
          body: params.body,
        }),
      };

      const res = await getFleetPerformanceAnalyticsListActions(
        globalFilters,
        apiParams,
      );
      return res?.responseStatus?.code === 200
        ? res.response?.data || (actionType === "download" ? [] : null)
        : actionType === "download"
          ? []
          : null;
    },

    level2GetTableData: async (
      level2Record,
      drillDownDataPoint,
      pageNumber,
      rowsPerPage,
      sortBy,
      globalFilters,
      selectedCustomFilters,
      options = {},
    ) => {
      if (!level2Record || !drillDownDataPoint) return { data: [], total: 0 };

      const apiParams = {
        groupBy: ["batteryID"],
        select: [
          "ChargeEnergyConsumedTotal",
          "DischargeEnergyConsumedTotal",
          "RegenEnergyTotal",
          "DistanceTotal",
        ],
        metaInfo: withConfiguredColumns(options, [
          "licencePlatNumber",
          "originCity",
          "originDepot",
          "originState",
          "deviceID",
          "pingStatus",
        ]),
        startDate: drillDownDataPoint.startDate,
        endDate: drillDownDataPoint.endDate,
        filters: [{ name: "ProjectName", values: [level2Record.col1] }],
        sortBy:
          sortBy && sortBy.length > 0 ? JSON.stringify(sortBy) : undefined,
        pageNum: pageNumber,
        pageCount: rowsPerPage,
        skipPagination: false,
      };

      if (selectedCustomFilters && Object.keys(selectedCustomFilters).length > 0) {
        Object.entries(selectedCustomFilters).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            apiParams.filters.push({ name: key, values: value });
          } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
            apiParams.filters.push({ name: key, min: value.min, max: value.max });
          }
        });
      }

      const response = await getFleetPerformanceAnalyticsList(
        globalFilters,
        apiParams,
      );
      if (response?.responseStatus?.code === 200) {
        const rawData = response.response?.data || [];
        const total =
          response.response?.paginationInfo?.total || rawData.length;
        const items = rawData.map((item) => {
          const getMetric = (type) =>
            item.metricsArray?.find((m) => m.type === type)?.value || 0;
          return {
            id: item.xLabel,
            ...item.metaInfo,
            ChargeEnergyConsumedTotal: getMetric("ChargeEnergyConsumedTotal"),
            DischargeEnergyConsumedTotal: getMetric(
              "DischargeEnergyConsumedTotal",
            ),
            RegenEnergyTotal: getMetric("RegenEnergyTotal"),
            DistanceTotal: getMetric("DistanceTotal"),
          };
        });
        return { data: items, total };
      }
      return { data: [], total: 0 };
    },

    level2ApplyTableAction: async (
      rowSelectionKeys,
      actionType,
      selectAll,
      params,
      level2Record,
      drillDownDataPoint,
      globalFilters,
      startDate,
      endDate,
      options = {},
    ) => {
      if (!level2Record || !drillDownDataPoint)
        return actionType === "download" ? [] : null;

      const apiParams = {
        actionType,
        groupBy: ["batteryID"],
        select: [
          "ChargeEnergyConsumedTotal",
          "DischargeEnergyConsumedTotal",
          "RegenEnergyTotal",
          "DistanceTotal",
        ],
        metaInfo: withConfiguredColumns(options, [
          "licencePlatNumber",
          "originCity",
          "originDepot",
          "originState",
          "deviceID",
          "pingStatus",
        ]),
        startDate: drillDownDataPoint.startDate,
        endDate: drillDownDataPoint.endDate,
        filters: [{ name: "ProjectName", values: [level2Record.col1] }],
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        columns: [
          { dataIndex: "licencePlatNumber", headerLabel: "Bus Number" },
          {
            dataIndex: "ChargeEnergyConsumedTotal",
            headerLabel: "Charge Energy Consumption (kWh)",
          },
          {
            dataIndex: "DischargeEnergyConsumedTotal",
            headerLabel: "Discharge Energy Consumption (kWh)",
          },
          { dataIndex: "RegenEnergyTotal", headerLabel: "Regeneration Energy (kWh)" },
          { dataIndex: "DistanceTotal", headerLabel: "Distance Covered (km)" },
          { dataIndex: "originDepot", headerLabel: "Depot" },
          { dataIndex: "originCity", headerLabel: "City" },
          { dataIndex: "originState", headerLabel: "State" },
        ],
        columnOrder: [
          "Bus Number",
          "Charge Energy Consumption (kWh)",
          "Discharge Energy Consumption (kWh)",
          "Regeneration Energy (kWh)",
          "Distance Covered (km)",
          "Depot",
          "City",
          "State",
        ],
        ...(actionType === "mail" && {
          emailTo: params.emailTo,
          emailCc: params.emailCc,
          subject: params.subject,
          body: params.body,
        }),
      };

      if (params?.selectedFilters && Object.keys(params.selectedFilters).length > 0) {
        Object.entries(params.selectedFilters).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            apiParams.filters.push({ name: key, values: value });
          } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
            apiParams.filters.push({ name: key, min: value.min, max: value.max });
          }
        });
      }

      const res = await getFleetPerformanceAnalyticsListActions(
        globalFilters,
        apiParams,
      );
      return res?.responseStatus?.code === 200
        ? res.response?.data || (actionType === "download" ? [] : null)
        : actionType === "download"
          ? []
          : null;
    },
  },
};